import { Button, Heading, ProgressCircle, Text } from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo, useState } from 'react';

import { AnimateIn } from '@/components/AnimateIn';
import { AddBusinessComposer } from '@/components/competitors/AddBusinessComposer';
import { CompetitorCard } from '@/components/competitors/CompetitorCard';
import { ResearchingCard } from '@/components/competitors/ResearchingCard';
import { InfoBanner } from '@/components/InfoBanner';
import { cn } from '@/utils/cn';

import type {
	EditableCompetitorArrayField,
	EditableCompetitorField,
	SocialPlatformKey,
} from '../../../../../bridge/types/competitor-fields.ts';
import type {
	Competitor,
	CompetitorProduct,
} from '../../../../../bridge/types/competitors.ts';
import { cardTitleStyles } from '../styles';

import { hasSubstantiveData } from './competitorData.ts';

// Best-effort display name for a still-researching card: the typed name, or the
// hostname of the seeded website when only a URL was given.
function researchingTitle(c: Competitor): string {
	if (c.name) return c.name;
	const url = c.website_url;
	if (!url) return 'New business';
	try {
		return new URL(url).hostname.replace(/^www\./, '');
	} catch {
		return url;
	}
}

// A seed with a website but no name was added by URL — its loading narration can
// skip the "find their website" step since the site is already known.
function researchingSeedKind(c: Competitor): 'name' | 'url' {
	return !c.name && !!c.website_url ? 'url' : 'name';
}

type CompetitorsTabProps = {
	competitors: Record<string, Competitor>;
	analyzingIds: Set<string>;
	isResearching: boolean;
	newCompetitorId: string | null;
	onUpdateCompetitor: (
		id: string,
		field: EditableCompetitorField,
		value: string,
	) => void;
	onUpdateCompetitorArrayField: (
		id: string,
		field: EditableCompetitorArrayField,
		value: string[],
	) => void;
	onUpdateCompetitorSocialUrl: (
		id: string,
		platform: SocialPlatformKey,
		url: string,
	) => void;
	onUpdateCompetitorProducts: (
		id: string,
		products: CompetitorProduct[],
	) => void;
	onRemoveCompetitor: (id: string) => void;
	onRunInsights: (id: string) => void;
	onResearchCompetitor: (query: string) => Promise<string | null>;
	researchingIds: Set<string>;
	researchedIds: Set<string>;
	failedResearchIds: Set<string>;
	onClearNewCompetitor: () => void;
	error: string | null;
	onRetry: () => void;
};

export const CompetitorsTab = memo(function CompetitorsTab({
	competitors,
	analyzingIds,
	isResearching,
	newCompetitorId,
	onUpdateCompetitor,
	onUpdateCompetitorArrayField,
	onUpdateCompetitorSocialUrl,
	onUpdateCompetitorProducts,
	onRemoveCompetitor,
	onRunInsights,
	onResearchCompetitor,
	researchingIds,
	researchedIds,
	failedResearchIds,
	onClearNewCompetitor,
	error,
	onRetry,
}: CompetitorsTabProps) {
	const competitorList = Object.values(competitors)
		.filter(
			(c) =>
				c.id === newCompetitorId ||
				researchingIds.has(c.id) ||
				researchedIds.has(c.id) ||
				!!c.last_researched_at ||
				hasSubstantiveData(c),
		)
		.reverse();

	const [expandedId, setExpandedId] = useState<string | null>(
		() => competitorList[0]?.id ?? null,
	);
	const [composerOpen, setComposerOpen] = useState(false);

	const handleSubmit = async (query: string) => {
		await onResearchCompetitor(query);
		setComposerOpen(false);
	};

	// Only replace the empty state, never a populated list: fetchCompetitors also
	// runs on background poll/revert refetches, so a transient failure while
	// businesses are on screen must not wipe them for an error screen.
	if (error && competitorList.length === 0) {
		return (
			<AnimateIn delay={0}>
				<div className={cn('glass-card-sm p-6', 'text-center')}>
					<div className="flex flex-col items-center gap-4">
						<Text
							styles={style({
								font: 'body',
								fontSize: 'body',
								color: 'neutral',
							})}
						>
							We couldn’t load your similar businesses. Please try again.
						</Text>
						<Button variant="accent" size="S" onPress={onRetry}>
							<Text>Try again</Text>
						</Button>
					</div>
				</div>
			</AnimateIn>
		);
	}

	if (competitorList.length === 0) {
		return (
			<AnimateIn delay={0}>
				{composerOpen ? (
					<AddBusinessComposer
						onSubmit={handleSubmit}
						onCancel={() => setComposerOpen(false)}
					/>
				) : (
					<div className={cn('glass-card-sm p-6', 'text-center')}>
						<div className="flex flex-col items-center gap-4">
							<Text
								styles={style({
									font: 'body',
									fontSize: 'body',
									color: 'neutral',
								})}
							>
								No similar business data available yet. Research on similar
								businesses will appear here after your business profile is
								analyzed.
							</Text>
							<Button
								variant="accent"
								size="S"
								onPress={() => setComposerOpen(true)}
							>
								<Add />
								<Text>Add business</Text>
							</Button>
						</div>
					</div>
				)}
			</AnimateIn>
		);
	}

	return (
		<AnimateIn delay={0}>
			<div className="flex flex-col gap-4">
				<div className="flex items-center justify-between">
					<Heading level={3} styles={cardTitleStyles}>
						Similar Businesses
					</Heading>
					<Button
						variant="primary"
						size="S"
						onPress={() => setComposerOpen(true)}
					>
						<Add />
						<Text>Add business</Text>
					</Button>
				</div>
				{composerOpen && (
					<AddBusinessComposer
						onSubmit={handleSubmit}
						onCancel={() => setComposerOpen(false)}
					/>
				)}
				{isResearching && (
					<InfoBanner
						icon={
							<ProgressCircle size="S" isIndeterminate aria-label="Analyzing" />
						}
					>
						<Text
							styles={style({
								font: 'body',
								fontSize: 'body',
								color: 'body',
							})}
						>
							Analyzing similar businesses&apos; Instagram...
						</Text>
					</InfoBanner>
				)}
				{competitorList.map((competitor) =>
					researchingIds.has(competitor.id) ? (
						<div key={competitor.id}>
							<ResearchingCard
								title={researchingTitle(competitor)}
								seedKind={researchingSeedKind(competitor)}
							/>
						</div>
					) : (
						<div key={competitor.id}>
							<CompetitorCard
								competitor={competitor}
								isExpanded={expandedId === competitor.id}
								onToggle={() =>
									setExpandedId((prev) =>
										prev === competitor.id ? null : competitor.id,
									)
								}
								showInsightsButton={true}
								isRunningInsights={analyzingIds.has(competitor.id)}
								insightsDisabled={analyzingIds.size > 0}
								onRunInsights={() => onRunInsights(competitor.id)}
								onUpdateCompetitor={onUpdateCompetitor}
								onUpdateCompetitorArrayField={onUpdateCompetitorArrayField}
								onUpdateCompetitorSocialUrl={onUpdateCompetitorSocialUrl}
								onUpdateCompetitorProducts={onUpdateCompetitorProducts}
								onRemoveCompetitor={onRemoveCompetitor}
								initialEditing={competitor.id === newCompetitorId}
								onClearNewCompetitor={onClearNewCompetitor}
								thinResult={
									failedResearchIds.has(competitor.id) ||
									(!!competitor.last_researched_at &&
										!hasSubstantiveData(competitor))
								}
							/>
						</div>
					),
				)}
			</div>
		</AnimateIn>
	);
});
